import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, ThemeEvents } from '@theme/events';

const MAX_SIZE = 14;
const MIN_SIZE = 6;
const STEP = 0.5;

/**
 * @typedef {object} PersonaliseMessageRefs
 * @property {HTMLElement} toggle
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} stage
 * @property {HTMLTextAreaElement} input
 * @property {HTMLElement} fullNote
 * @property {HTMLElement} error
 * @property {HTMLButtonElement} submit
 *
 * @extends Component<PersonaliseMessageRefs>
 */
class PersonaliseMessageComponent extends Component {
  requiredRefs = ['toggle', 'dialog', 'stage', 'input', 'fullNote', 'error', 'submit'];

  #abortController = new AbortController();
  #active = false;
  #busy = false;
  #pressedOnBackdrop = false;
  #size = MAX_SIZE;
  #lastValue = '';
  #lastSelection = [0, 0];
  #touch = window.matchMedia('(pointer: coarse)');

  /** @type {HTMLFormElement | null} */
  #mainForm = null;
  #originalLabel = '';

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const root = this.closest('.shopify-section') ?? document;

    this.#mainForm = root.querySelector('.buy-buttons-block form[data-type="add-to-cart-form"]');

    window.addEventListener('click', this.#onMainClick, { capture: true, signal });
    this.#mainForm?.addEventListener('submit', this.#onMainSubmit, { capture: true, signal });
    root.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    const { dialog, input } = this.refs;
    dialog.addEventListener('pointerdown', this.#onDialogPointerDown, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('close', this.#onClosed, { signal });
    input.addEventListener('beforeinput', this.#rememberSelection, { signal });

    if (this.#touch.matches && this.dataset.placeholderTouch) input.placeholder = this.dataset.placeholderTouch;

    const resizeObserver = new ResizeObserver(() => this.#updateScale());
    resizeObserver.observe(this.refs.stage);
    signal.addEventListener('abort', () => resizeObserver.disconnect());

    this.#syncAvailability();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  toggle() {
    if (this.#soldOut()) return;
    this.#setActive(!this.#active);
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    this.toggle();
  }

  open() {
    const { dialog, input } = this.refs;
    if (!dialog.open) dialog.showModal();
    this.#fit();
    this.refs.fullNote.hidden = input.value.length < input.maxLength;
    this.refs.error.hidden = true;
    if (!this.#touch.matches) input.focus();
  }

  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  handleFocus() {
    setTimeout(() => this.refs.input.scrollIntoView({ block: 'center', behavior: 'smooth' }), 300);
  }

  handleInput() {
    const { input, fullNote } = this.refs;

    if (this.#fit()) {
      this.#lastValue = input.value;
      fullNote.hidden = input.value.length < input.maxLength;
    } else {
      input.value = this.#lastValue;
      input.setSelectionRange(this.#lastSelection[0], this.#lastSelection[1]);
      this.#fit();
      fullNote.hidden = false;
    }

    this.#syncSubmit();
  }

  async handleAdd() {
    if (this.#busy) return;

    const message = this.#messageValue();
    if (!message.trim()) return;

    const { submit, error } = this.refs;
    this.#busy = true;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    submit.classList.add('atc-added');
    error.hidden = true;

    /** @type {{ id: number | string, quantity: number, properties?: Record<string, string> }[]} */
    const items = [
      {
        id: this.#cardVariantId(),
        quantity: 1,
        properties: { 'Personal message': message, '_Font size': String(this.#size) },
      },
    ];

    const standActive = document.querySelector('.product_custom_option_wrapper.product-block.active_block');
    const standId = document.querySelector('input.additional_product')?.getAttribute('value');
    if (standActive && standId) items.push({ id: standId, quantity: 1 });

    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections }),
      });
      const data = await response.json();

      if (data.status) {
        submit.classList.remove('atc-added');
        this.#showError(data.message);
        window.dispatchEvent(new CartErrorEvent(this.id, data.message));
        return;
      }

      this.close();
      this.dispatchEvent(
        new CartAddEvent({}, this.id, {
          source: 'personalise-add',
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch {
      submit.classList.remove('atc-added');
      this.#showError(this.dataset.errorText ?? '');
    } finally {
      this.#busy = false;
      this.refs.submit.removeAttribute('aria-busy');
      this.#syncSubmit();
    }
  }

  /** @param {boolean} active */
  #setActive(active) {
    this.#active = active;
    const { toggle } = this.refs;

    toggle.setAttribute('aria-checked', String(active));
    toggle.classList.toggle('active_block', active);
    toggle.querySelector('.icon-checkmark')?.classList.toggle('hidden', !active);
    toggle.querySelector('.icon-plus')?.classList.toggle('hidden', active);

    this.#applyLabel();
  }

  #applyLabel() {
    const label = this.#mainForm?.querySelector('.add-to-cart-text__content');
    if (!label) return;

    const current = label.textContent?.trim() ?? '';

    if (!this.#active) {
      if (this.#originalLabel && current === this.dataset.buttonLabel) label.textContent = this.#originalLabel;
      return;
    }

    if (current !== this.dataset.buttonLabel) this.#originalLabel = current;
    label.textContent = this.dataset.buttonLabel ?? current;
  }

  /** @param {Event} event */
  #onMainClick = (event) => {
    if (!this.#active || !this.#mainForm) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button[name="add"], .add-to-cart-button');
    if (!button || !this.#mainForm.contains(button)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  };

  /** @param {Event} event */
  #onMainSubmit = (event) => {
    if (!this.#active) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.open();
  };

  #onVariantUpdate = () => {
    setTimeout(() => {
      if (this.#active && this.#soldOut()) this.#setActive(false);
      else this.#applyLabel();

      this.#syncAvailability();
    }, 0);
  };

  #soldOut() {
    return Boolean(this.#mainForm?.querySelector('button[name="add"]')?.disabled);
  }

  #syncAvailability() {
    this.refs.toggle.setAttribute('aria-disabled', String(this.#soldOut()));
  }

  /** @param {PointerEvent} event */
  #onDialogPointerDown = (event) => {
    this.#pressedOnBackdrop = event.target === this.refs.dialog;
  };

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const closeOnBackdrop = event.target === this.refs.dialog && this.#pressedOnBackdrop;

    this.#pressedOnBackdrop = false;
    if (closeOnBackdrop) this.close();
  };

  #onClosed = () => {
    this.refs.submit.classList.remove('atc-added');
    this.refs.fullNote.hidden = true;
  };

  #rememberSelection = () => {
    const { input } = this.refs;
    this.#lastSelection = [input.selectionStart, input.selectionEnd];
  };

  #cardVariantId() {
    const field = this.#mainForm?.querySelector('[name="id"]');
    return field instanceof HTMLInputElement && field.value ? field.value : (this.dataset.variantId ?? '');
  }

  #syncSubmit() {
    this.refs.submit.disabled = this.#busy || !this.refs.input.value.trim();
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = false;
  }

  /** @param {number} size */
  #applySize(size) {
    this.#size = size;
    this.refs.stage.style.setProperty('--pm-ref', String(size));
    this.#updateScale();
  }

  /** Touch devices type in a 16px font (avoids the iOS focus zoom); this scales it down to the on-card size. */
  #updateScale() {
    const { stage } = this.refs;
    const width = stage.getBoundingClientRect().width;
    if (!width) return;

    stage.style.setProperty('--pm-scale', String((this.#size * width) / 444 / 16));
  }

  #overflows() {
    const { input } = this.refs;
    return input.scrollHeight > input.clientHeight + 1;
  }

  /** @returns {boolean} */
  #fit() {
    let size = MAX_SIZE;
    this.#applySize(size);

    while (this.#overflows() && size > MIN_SIZE) {
      size -= STEP;
      this.#applySize(size);
    }

    return !this.#overflows();
  }

  /** @returns {string} */
  #messageValue() {
    return this.refs.input.value
      .replace(/\r\n?/g, '\n')
      .replace(/[ \t]+$/gm, '')
      .replace(/\s+$/, '');
  }
}

if (!customElements.get('personalise-message-component')) {
  customElements.define('personalise-message-component', PersonaliseMessageComponent);
}
